import React from 'react';
import { createRoot } from 'react-dom/client';

// Import all components (based on your original packs/application.js)
import PhoneInput from 'react-phone-input-2';
import PasswordInput from './components/PasswordInput';
import StyledTextField from './components/common/StyledTextField';
import PhoneNumberInput from './components/common/PhoneNumberInput';
import AskQuestion from './components/AskQuestion';
import ProjectFeedbacks from './components/ProjectFeedbacks';
import ProposalFeedbacks from './components/ProposalFeedbacks';
import ScheduleInterviewModal from './components/ScheduleInterviewModal';
import ScheduleInterviewConsultant from './components/ScheduleInterviewConsultant';
import DeclineProposal from './components/DeclineProposal';
import AcceptProposal from './components/AcceptProposal';
import SubmitToEmployer from './components/SubmitToEmployer';
import ProposalResponse from './components/ProposalResponse';
import ShareResource from './components/ShareResource';
import CopyLink from './components/common/CopyLink';
import NotFound from './components/NotFound';
import CreateProposal from './components/Proposals/CreateProposal';
import ShortDescription from './components/ShortDescription';
import TextAreaMaxWords from './components/common/TextAreaMaxWords';
import YesNoNumberInput from './components/common/YesNoNumberInput';
import ConfirmSubmitProposal from './components/common/ConfirmSubmitProposal';
import ModalTrigger from './components/common/ModalTrigger';
import ProjectWorkLocationQuestion from './components/ProjectWorkLocationQuestion';
import TitleTabBar from './components/Proposals/TitleTabBar';
import ProjectIndexContainer from './components/ProjectIndexContainer';
import ConsultantProjectIndexContainer from './components/ConsultantProjectIndexContainer';
import ProjectIndexListGroup from './components/ProjectIndexListGroup';
import MarkOnHold from './components/MarkOnHold';
import OnboardingFlow from './components/ConsultantOnboarding/OnboardingFlow';
import UserSection from './components/ConsultantProfile/UserSection';
import ConsultantProfile from './components/ConsultantProfile';
import AnswersContainer from './components/AnswersContainer';
import Opportunity from './components/Opportunity';
import ToggleUpcomingNotification from './components/ToggleUpcomingNotification';
import Registration from './components/Registration';
import EmployerSignupForm from './components/Registration/EmployerSignupForm';
import SignInForm from './components/SignInForm';
import EmployerProjectIndexContainer from './components/EmployerProjectIndexContainer';
import HowCloraWorksForEmployers from './components/HowCloraWorksForEmployers';
import InterestInProjectSubmitted from './components/InterestInProjectSubmitted';
import ReferralsIndexContainer from './components/ReferralsIndexContainer';
import ReferralTrigger from './components/ReferralTrigger';
import ReferralOptions from './components/ReferralsIndexContainer/ReferralOptions';
import ReadOnlyInputWithEditLink from './components/ReadOnlyInputWithEditLink';
import ProjectShare from './components/ProjectShare';
import ConsultantReferralForm from './components/ConsultantReferralForm';
import RepProjectApplicationActions from './components/RepProjectApplicationActions';
import ConsultantInterest from './components/ConsultantInterest';
import ConsultantCreateConsultantInterest from './components/ConsultantCreateConsultantInterest';
import RecommendationsReceivedModal from './components/RecommendationsReceivedModal';
import DocumentUpload from './components/DocumentUpload';
import ProjectLead from './components/ProjectLead';
import AddProposalPastRoles from './components/AddProposalPastRoles';
import AddLocationOnProposal from './components/AddLocationOnProposal';
import ProfileCompletion from './components/common/ProfileCompletion';
import SubmitProposal from './components/SubmitProposal';
import ConsultantCard from './components/ConsultantCard';
import EmptyFeaturedProfiles from './components/EmptyFeaturedProfiles';
import ProjectPublishedModal from './components/ProjectPublishedModal';
import ProjectStatusBar from './components/ProjectStatusBar';
import ProjectStatusActions from './components/ProjectStatusActions';
import Invoice from './components/Invoice';
import DisplayLineItem from './components/common/DisplayLineItem';
import InvoiceHeader from './components/Invoice/InvoiceHeader';
import ProjectNameSummaryModal from './components/ProjectNameSummaryModal';
import RequestEditsInvoice from './components/RequestEditsInvoice/RequestEditsInvoice';
import InvoicesWalkthrough from './components/InvoicesWalkthrough';
import ExecuteAgreementForProject from './components/ExecuteAgreementForProject';
import WorkCompletedForProject from './components/WorkCompletedForProject';
import EditInvoiceRate from './components/EditInvoiceRate';
import NotInvoicingModal from './components/NotInvoicingModal';
import Review from './components/Review';
import EmployerInvitesConsultantToProjectModal from './components/EmployerInvitesConsultantToProjectModal';
import ReviewIndexPage from './components/ReviewIndexPage';
import ContactSupportBanner from './components/ContactSupportBanner';
import AutosuggestInput from './components/common/AutosuggestInput';
import ProjectInvitationCard from './components/ProjectInvitationCard';
import ProjectInvitationAskQuestion from './components/ProjectInvitationAskQuestion';
import ProjectInvitationDecline from './components/ProjectInvitationDecline';
import ProjectPermissions from './components/ProjectPermissions';
import LikeDislikeProject from './components/common/LikeDislikeProject';
import AdminCreateInvoiceModal from './components/AdminCreateInvoiceModal';
import NPSModal from './components/NPSModal';
import EmployerReferralModal from './components/EmployerReferralModal';
import AddCoworkersModal from './components/AddCoworkersModal';
import AddProject from './components/AddProject';
import ConsultantTalentContainer from './components/ConsultantTalentContainer';
import HireAgain from './components/HireAgain';
import ProfileSuggestions from './components/common/ProfileSuggestions';
import ProposalInterviews from './components/ProposalInterviews';
import EmployerInterviews from './components/EmployerInterviews';
import InterviewPanel from './components/InterviewPanel';
import ProfileSuggestionModal from './components/ProfileSuggestionModal';
import NavBar from './components/ProjectDetails/NavBar';
import ProjectName from './components/ProjectDetails/ProjectName';
import ProjectExpertise from './components/ProjectDetails/ProjectExpertise';
import ProjectExpertisePriority from './components/ProjectDetails/ProjectExpertisePriority';
import ProjectDetails from './components/ProjectDetails';
import UserViewsTable from './components/UserViewsTable';
import InfoTooltip from './components/common/InfoTooltip';
import ConsultantReview from './components/ConsultantReview';
import ConfirmModal from './components/ConfirmModal';
import FeedbackHistory from './components/FeedbackHistory';
import FeedbackHistoryRow from './components/FeedbackHistory/FeedbackHistoryRow';
import TypeformModal from './components/TypeformModal';
import ProjectRepAssigned from './components/ProjectRepAssigned/ProjectRepAssigned';
import ErrorMessageField from './components/common/ErrorMessageField';
import ConsultantAgreementModal from './components/ConsultantAgreementModal';
import FilterTag from './components/common/FilterTag';
import ProjectNav from './components/ProjectNav';
import RecommendedConsultantCard from './components/RecommendedConsultantCard';
import RegistrationModal from './components/RegistrationModal';
import SavedFeaturedProfile from './components/SavedFeaturedProfile';
import ProjectClosure from './components/ProjectClosure';
import ProjectStatusBox from './components/ProjectStatusBox/ProjectStatusBox';
import StatusChange from './components/StatusChange';
import ProjectEmploymentType from './components/common/ProjectEmploymentType';
import CompanyMerge from './components/CompanyMerge';
import ProjectLogisticsInfo from './components/common/ProjectLogisticsInfo';
import SignIn from './components/SignIn';
import NextSteps from './components/ProjectNav/NextSteps';
import GetStartedCTA from './components/common/GetStartedCTA';
import Wall from './components/Registration/Wall';
import Resume from './components/common/Resume';
import CompanyRegistrationDropdown from './components/common/CompanyRegistrationDropdown';
import CreateInvoiceDropdown from './components/CreateInvoiceDropdown';
import ViewAdHocInvoiceDropdown from './components/ViewAdHocInvoiceDropdown';
import ProposalQuestion from './components/common/ProposalQuestion';
import ProposalShow from './components/common/ProposalShow/ProposalShow';
import ProposalMessages from './components/ProposalMessages/ProposalMessages';
import MarketingMessages from './components/MarketingMessages';
import MessageNotifications from './components/MessageNotifications/MessageNotifications';
import UserFavorites from './components/UserFavorites';
import InterviewQuestions from './components/InterviewQuestions';
import DiseaseCodeMerge from './components/DiseaseCodeMerge';

// Component registry - maps component names to actual components
var components = {
  PasswordInput: PasswordInput,
  StyledTextField: StyledTextField,
  PhoneInput: PhoneInput,
  PhoneNumberInput: PhoneNumberInput,
  AskQuestion: AskQuestion,
  ProjectFeedbacks: ProjectFeedbacks,
  ProposalFeedbacks: ProposalFeedbacks,
  ScheduleInterviewModal: ScheduleInterviewModal,
  ScheduleInterviewConsultant: ScheduleInterviewConsultant,
  DeclineProposal: DeclineProposal,
  AcceptProposal: AcceptProposal,
  SubmitToEmployer: SubmitToEmployer,
  ProposalResponse: ProposalResponse,
  ShareResource: ShareResource,
  CopyLink: CopyLink,
  NotFound: NotFound,
  CreateProposal: CreateProposal,
  ShortDescription: ShortDescription,
  TextAreaMaxWords: TextAreaMaxWords,
  YesNoNumberInput: YesNoNumberInput,
  ConfirmSubmitProposal: ConfirmSubmitProposal,
  ModalTrigger: ModalTrigger,
  ProjectWorkLocationQuestion: ProjectWorkLocationQuestion,
  TitleTabBar: TitleTabBar,
  ProjectIndexContainer: ProjectIndexContainer,
  ConsultantProjectIndexContainer: ConsultantProjectIndexContainer,
  ProjectIndexListGroup: ProjectIndexListGroup,
  MarkOnHold: MarkOnHold,
  OnboardingFlow: OnboardingFlow,
  UserSection: UserSection,
  ConsultantProfile: ConsultantProfile,
  AnswersContainer: AnswersContainer,
  Opportunity: Opportunity,
  ToggleUpcomingNotification: ToggleUpcomingNotification,
  Registration: Registration,
  EmployerSignupForm: EmployerSignupForm,
  SignInForm: SignInForm,
  EmployerProjectIndexContainer: EmployerProjectIndexContainer,
  HowCloraWorksForEmployers: HowCloraWorksForEmployers,
  InterestInProjectSubmitted: InterestInProjectSubmitted,
  ReferralsIndexContainer: ReferralsIndexContainer,
  ReferralTrigger: ReferralTrigger,
  ReferralOptions: ReferralOptions,
  ReadOnlyInputWithEditLink: ReadOnlyInputWithEditLink,
  ProjectShare: ProjectShare,
  RepProjectApplicationActions: RepProjectApplicationActions,
  ConsultantInterest: ConsultantInterest,
  ConsultantCreateConsultantInterest: ConsultantCreateConsultantInterest,
  ConsultantReferralForm: ConsultantReferralForm,
  DocumentUpload: DocumentUpload,
  ProjectLead: ProjectLead,
  AddProposalPastRoles: AddProposalPastRoles,
  AddLocationOnProposal: AddLocationOnProposal,
  ProfileCompletion: ProfileCompletion,
  SubmitProposal: SubmitProposal,
  ConsultantCard: ConsultantCard,
  EmptyFeaturedProfiles: EmptyFeaturedProfiles,
  ProjectPublishedModal: ProjectPublishedModal,
  ProjectStatusBar: ProjectStatusBar,
  ProjectStatusActions: ProjectStatusActions,
  Invoice: Invoice,
  DisplayLineItem: DisplayLineItem,
  InvoiceHeader: InvoiceHeader,
  ProjectNameSummaryModal: ProjectNameSummaryModal,
  RequestEditsInvoice: RequestEditsInvoice,
  InvoicesWalkthrough: InvoicesWalkthrough,
  ExecuteAgreementForProject: ExecuteAgreementForProject,
  WorkCompletedForProject: WorkCompletedForProject,
  EditInvoiceRate: EditInvoiceRate,
  NotInvoicingModal: NotInvoicingModal,
  Review: Review,
  EmployerInvitesConsultantToProjectModal: EmployerInvitesConsultantToProjectModal,
  ReviewIndexPage: ReviewIndexPage,
  ContactSupportBanner: ContactSupportBanner,
  AutosuggestInput: AutosuggestInput,
  ProjectInvitationCard: ProjectInvitationCard,
  ProjectInvitationAskQuestion: ProjectInvitationAskQuestion,
  ProjectInvitationDecline: ProjectInvitationDecline,
  ProjectPermissions: ProjectPermissions,
  LikeDislikeProject: LikeDislikeProject,
  AdminCreateInvoiceModal: AdminCreateInvoiceModal,
  NPSModal: NPSModal,
  EmployerReferralModal: EmployerReferralModal,
  AddCoworkersModal: AddCoworkersModal,
  AddProject: AddProject,
  ConsultantTalentContainer: ConsultantTalentContainer,
  HireAgain: HireAgain,
  ProfileSuggestions: ProfileSuggestions,
  ProposalInterviews: ProposalInterviews,
  EmployerInterviews: EmployerInterviews,
  InterviewPanel: InterviewPanel,
  ProfileSuggestionModal: ProfileSuggestionModal,
  NavBar: NavBar,
  ProjectDetails: ProjectDetails,
  UserViewsTable: UserViewsTable,
  InfoTooltip: InfoTooltip,
  ConsultantReview: ConsultantReview,
  ConfirmModal: ConfirmModal,
  FeedbackHistory: FeedbackHistory,
  FeedbackHistoryRow: FeedbackHistoryRow,
  TypeformModal: TypeformModal,
  ProjectRepAssigned: ProjectRepAssigned,
  ErrorMessageField: ErrorMessageField,
  StatusChange: StatusChange,
  ConsultantAgreementModal: ConsultantAgreementModal,
  SavedFeaturedProfile: SavedFeaturedProfile,
  ProjectClosure: ProjectClosure,
  FilterTag: FilterTag,
  ProjectNav: ProjectNav,
  ProjectStatusBox: ProjectStatusBox,
  RecommendedConsultantCard: RecommendedConsultantCard,
  RegistrationModal: RegistrationModal,
  ProjectEmploymentType: ProjectEmploymentType,
  CompanyMerge: CompanyMerge,
  ProjectLogisticsInfo: ProjectLogisticsInfo,
  SignIn: SignIn,
  NextSteps: NextSteps,
  GetStartedCTA: GetStartedCTA,
  Wall: Wall,
  Resume: Resume,
  CompanyRegistrationDropdown: CompanyRegistrationDropdown,
  CreateInvoiceDropdown: CreateInvoiceDropdown,
  ViewAdHocInvoiceDropdown: ViewAdHocInvoiceDropdown,
  ProposalQuestion: ProposalQuestion,
  ProposalShow: ProposalShow,
  ProposalMessages: ProposalMessages,
  MarketingMessages: MarketingMessages,
  MessageNotifications: MessageNotifications,
  ProjectExpertise: ProjectExpertise,
  ProjectExpertisePriority: ProjectExpertisePriority,
  RecommendationsReceivedModal: RecommendationsReceivedModal,
  ProjectName: ProjectName,
  UserFavorites: UserFavorites,
  InterviewQuestions: InterviewQuestions,
  DiseaseCodeMerge: DiseaseCodeMerge
};
var mountedRoots = new WeakMap();

// Mount function that replaces WebpackerReact
function mountComponents() {
  document.querySelectorAll('[data-react-component]').forEach(function (node) {
    var componentName = node.dataset.reactComponent;
    var Component = components[componentName];
    if (Component) {
      try {
        var props = JSON.parse(node.dataset.reactProps || '{}');
        var root = mountedRoots.get(node);
        if (!root) {
          root = createRoot(node);
          mountedRoots.set(node, root);
        }
        root.render(/*#__PURE__*/React.createElement(Component, props));
      } catch (error) {
        console.error("Error mounting component ".concat(componentName, ":"), error);
      }
    } else if (process.env.NODE_ENV === 'development') {
      // May be handled by a page-specific bundle (e.g. components-search.js)
      console.debug("[components] ".concat(componentName, " not in main registry \u2014 checking sibling bundles"));
    }
  });
}

// Mount on DOMContentLoaded and Turbo events (if using Turbo)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountComponents);
} else {
  mountComponents();
}

// Support for Turbo Drive (Rails 8 default)
document.addEventListener('turbo:load', mountComponents);
document.addEventListener('turbo:render', mountComponents);